import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CreditCard, Plus, Search, XCircle } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { useReservas } from '@/data/hooks/useReservas';
import { useSucursales } from '@/data/hooks/useCatalogos';
import { useSession } from '@/core/auth/useSession';
import { ESTADOS_RESERVA, } from '@/data/dto/reserva';
import { ROUTES } from '@/core/config/routes';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { DatePicker } from '@/shared/components/DatePicker';
import { Input } from '@/shared/components/Input';
import { Pagination } from '@/shared/components/Pagination';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { formatearFecha, formatearFechaHora, formatearHora, formatearMonto, formatearPct, } from '@/core/utils/formatters';
import { ChipEstadoReserva, LABEL_ESTADO_RESERVA, LABEL_TIPO_CLASE, } from './components/chips';
import { ClienteAutocomplete } from './components/ClienteAutocomplete';
import { ReservaPreviewModal } from './components/ReservaPreviewModal';
import { RegistrarPagoModal } from './components/RegistrarPagoModal';
import { CancelarReservaModal } from './components/CancelarReservaModal';
const POR_PAGINA = 20;
function isoLocal(d) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function hoyIso() {
    return isoLocal(new Date());
}
function lunesDeEstaSemana(d) {
    const x = new Date(d);
    const dia = (x.getDay() + 6) % 7; // 0=lunes
    x.setDate(x.getDate() - dia);
    x.setHours(0, 0, 0, 0);
    return x;
}
function rangoDesdeOpcion(opcion) {
    const hoy = new Date();
    switch (opcion) {
        case 'hoy':
            return { desde: hoyIso(), hasta: hoyIso() };
        case 'semana': {
            const lunes = lunesDeEstaSemana(hoy);
            const dom = new Date(lunes);
            dom.setDate(dom.getDate() + 6);
            return { desde: isoLocal(lunes), hasta: isoLocal(dom) };
        }
        case 'proximos30': {
            const fin = new Date(hoy);
            fin.setDate(fin.getDate() + 30);
            return { desde: hoyIso(), hasta: isoLocal(fin) };
        }
        case 'pasadas': {
            const ayer = new Date(hoy);
            ayer.setDate(ayer.getDate() - 1);
            return { hasta: isoLocal(ayer) };
        }
        case 'todas':
        case 'rango':
            return {};
    }
}
function mesActualYYYYMM() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function rangoMes(yyyymm) {
    const partes = yyyymm.split('-');
    const y = Number(partes[0]);
    const m = Number(partes[1]);
    const desde = new Date(y, m - 1, 1);
    const hasta = new Date(y, m, 0);
    return { desde: isoLocal(desde), hasta: isoLocal(hasta) };
}
const LABEL_CANAL = {
    GESTION: 'Gestión',
    WEB: 'Web',
};
const QUICK_CHIPS = [
    { value: 'hoy', label: 'Hoy' },
    { value: 'semana', label: 'Esta semana' },
    { value: 'proximos30', label: 'Próximos 30d' },
    { value: 'pasadas', label: 'Pasadas' },
    { value: 'todas', label: 'Todas' },
    { value: 'rango', label: 'Rango…' },
];
export function ReservasPage() {
    const navigate = useNavigate();
    const { tieneRol, esPromotor, esPromotorExterno } = useSession();
    const puedeCrear = tieneRol('ROLE_CREAR_RESERVA') && (!esPromotor || esPromotorExterno);
    const puedePagar = tieneRol('ROLE_REGISTRAR_PAGO');
    const puedeCancelar = tieneRol('ROLE_CANCELAR_RESERVA');
    // Vista "mis reservas" para promotores (interno o externo): filtran por
    // mes de creación en vez de día de clase, ven comisión por reserva y
    // totalizador. El backend ya restringe a sus reservas via UsuarioScope.
    const vistaPromotor = esPromotor;
    const { data: sucursales } = useSucursales();
    const [sucursalId, setSucursalId] = useState('');
    const [estado, setEstado] = useState('');
    const [rangoOpcion, setRangoOpcion] = useState('hoy');
    const [rangoDesde, setRangoDesde] = useState('');
    const [rangoHasta, setRangoHasta] = useState('');
    const [busquedaCodigo, setBusquedaCodigo] = useState('');
    const [cliente, setCliente] = useState(null);
    const [mes, setMes] = useState(mesActualYYYYMM());
    const [estadoPago, setEstadoPago] = useState('');
    const [pagina, setPagina] = useState(1);
    const rangoComputado = useMemo(() => {
        if (vistaPromotor)
            return null;
        if (rangoOpcion === 'rango') {
            return { desde: rangoDesde || undefined, hasta: rangoHasta || undefined };
        }
        return rangoDesdeOpcion(rangoOpcion);
    }, [vistaPromotor, rangoOpcion, rangoDesde, rangoHasta]);
    const rangoCreado = useMemo(() => (vistaPromotor ? rangoMes(mes) : null), [vistaPromotor, mes]);
    const { data, isLoading, isError, error, refetch } = useReservas({
        sucursalId: sucursalId || undefined,
        clienteId: cliente?.id,
        estado: estado || undefined,
        q: busquedaCodigo.trim() || undefined,
        fechaClaseDesde: rangoComputado?.desde,
        fechaClaseHasta: rangoComputado?.hasta,
        creadoDesde: rangoCreado?.desde,
        creadoHasta: rangoCreado?.hasta,
        pagina,
        porPagina: POR_PAGINA,
    });
    const itemsFiltrados = useMemo(() => {
        if (!data)
            return [];
        if (!estadoPago)
            return data.items;
        return data.items.filter((r) => r.pagoEstado === estadoPago);
    }, [data, estadoPago]);
    const totales = useMemo(() => {
        if (!vistaPromotor)
            return null;
        let comision = 0;
        for (const r of itemsFiltrados) {
            if (r.estado === 'CANCELADA')
                continue;
            const c = r.comisionEstimada;
            if (c != null && c !== '')
                comision += Number(c);
        }
        return { comision };
    }, [itemsFiltrados, vistaPromotor]);
    const [pagoReserva, setPagoReserva] = useState(null);
    const [cancelarReserva, setCancelarReserva] = useState(null);
    const [previewReserva, setPreviewReserva] = useState(null);
    const columnas = useMemo(() => {
        const cols = [
            {
                key: 'codigo',
                header: 'Código',
                width: '170px',
                cell: (r) => (_jsxs("div", { className: "flex flex-col gap-0.5", children: [_jsxs("div", { className: "flex items-center gap-1.5", children: [_jsx(Link, { to: ROUTES.reservaDetalle(r.id), onClick: (e) => e.stopPropagation(), className: "font-mono text-[11px] text-primary hover:underline", children: r.codigo }), _jsx("span", { className: r.canal === 'WEB'
                                        ? 'rounded-pill bg-info-fill px-1.5 py-0 text-[9px] font-semibold text-info'
                                        : 'rounded-pill bg-surface-2 px-1.5 py-0 text-[9px] font-semibold text-text-tertiary', title: `Canal de origen: ${LABEL_CANAL[r.canal] ?? r.canal}`, children: LABEL_CANAL[r.canal] ?? r.canal })] }), r.claseDestacada ? _jsx(ClaseDestacadaLine, { info: r.claseDestacada }) : null] })),
            },
            {
                key: 'cliente',
                header: 'Cliente',
                cell: (r) => r.clienteNombre ? (_jsx("span", { className: "text-sm text-text-primary", children: r.clienteNombre })) : (_jsxs("span", { className: "text-text-tertiary", children: [r.clienteId.slice(0, 8), "\u2026"] })),
            },
            {
                key: 'estado',
                header: 'Estado',
                width: '130px',
                cell: (r) => _jsx(ChipEstadoReserva, { estado: r.estado }),
            },
        ];
        if (!vistaPromotor) {
            cols.push({
                key: 'clases',
                header: 'Clases',
                width: '120px',
                cell: (r) => _jsx(ResumenClases, { resumen: r.clasesResumen }),
            });
        }
        if (!vistaPromotor) {
            cols.push({
                key: 'pago',
                header: 'Pago',
                width: '150px',
                align: 'right',
                cell: (r) => (_jsxs("div", { className: "flex flex-col items-end gap-0.5", children: [_jsx("span", { className: "num text-sm font-semibold text-text-primary", children: r.totalNeto != null ? formatearMonto(r.totalNeto) : '—' }), _jsxs("div", { className: "flex items-center gap-1", children: [r.pagoEstado === 'parcial' ? _jsx(Chip, { tono: "warning", children: "Parcial" }) : null, r.paga ? (_jsx("span", { className: "text-[11px] font-semibold text-success", children: r.pasarelaPaga?.etiqueta ?? 'PAGA' })) : r.pagoEstado !== 'parcial' ? (_jsx("span", { className: "text-[11px] text-text-tertiary", children: r.metodoPagoPrevisto })) : null] })] })),
            });
        }
        if (vistaPromotor) {
            cols.push({
                key: 'comision',
                header: 'Comisión',
                width: '140px',
                align: 'right',
                cell: (r) => {
                    const comision = r.comisionEstimada != null ? Number(r.comisionEstimada) : 0;
                    const pct = formatearPct(r.comisionPctEfectiva ?? null);
                    return (_jsxs("div", { className: "flex flex-col items-end", children: [_jsx("span", { className: "num text-sm font-semibold text-success", children: formatearMonto(comision) }), _jsx("span", { className: "text-[11px] text-text-tertiary", children: pct })] }));
                },
            });
        }
        cols.push({
            key: 'creado',
            header: 'Creada',
            width: '150px',
            cell: (r) => (_jsx("span", { className: "text-[11px] text-text-tertiary", children: formatearFechaHora(r.creadoEn) })),
        });
        if (!vistaPromotor) {
            cols.push({
                key: 'acciones',
                header: 'Acciones',
                width: '170px',
                align: 'right',
                cell: (r) => (_jsxs("div", { className: "flex items-center justify-end gap-1", children: [puedePagar && r.estado !== 'CANCELADA' && r.estado !== 'FINALIZADA' && !r.paga ? (_jsxs(Button, { variant: "ghost", size: "sm", className: "h-7 px-2 text-[11px]", onClick: (e) => {
                                e.stopPropagation();
                                setPagoReserva(r);
                            }, children: [_jsx(CreditCard, { className: "h-3 w-3" }), "Pago"] })) : null, puedeCancelar && r.estado !== 'CANCELADA' && r.estado !== 'FINALIZADA' ? (_jsxs(Button, { variant: "ghost", size: "sm", className: "h-7 px-2 text-[11px] text-danger", onClick: (e) => {
                                e.stopPropagation();
                                setCancelarReserva(r);
                            }, children: [_jsx(XCircle, { className: "h-3 w-3" }), "Cancelar"] })) : null] })),
            });
        }
        return cols;
    }, [puedePagar, puedeCancelar, vistaPromotor]);
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-bold text-text-primary", children: vistaPromotor ? 'Mis reservas' : 'Reservas' }), _jsx("p", { className: "text-sm text-text-secondary", children: vistaPromotor
                                    ? 'Reservas que trajiste, filtradas por mes de creación.'
                                    : 'Listado de reservas. Filtrá por período, cliente o buscá por código.' })] }), puedeCrear ? (_jsxs(Button, { onClick: () => navigate('/reservas/nueva'), children: [_jsx(Plus, { className: "h-4 w-4" }), "Nueva reserva"] })) : null] }), _jsxs("div", { className: "flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface p-3", children: [vistaPromotor ? null : (_jsx("div", { className: "flex flex-wrap items-center gap-1.5", children: QUICK_CHIPS.map((opt) => (_jsx("button", { type: "button", onClick: () => {
                                setRangoOpcion(opt.value);
                                setPagina(1);
                            }, className: opt.value === rangoOpcion
                                ? 'rounded-pill bg-primary px-3 py-1 text-xs font-semibold text-primary-contrast'
                                : 'rounded-pill border border-border-subtle bg-surface px-3 py-1 text-xs font-medium text-text-secondary hover:bg-surface-2', children: opt.label }, opt.value))) })), _jsxs("div", { className: "flex flex-wrap items-end gap-3", children: [vistaPromotor ? (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Mes" }), _jsx("input", { type: "month", value: mes, onChange: (e) => {
                                            setMes(e.target.value || mesActualYYYYMM());
                                            setPagina(1);
                                        }, className: "h-9 w-40 rounded-md border border-border-subtle bg-surface px-2 text-sm text-text-primary focus:border-primary focus:outline-none" })] })) : rangoOpcion === 'rango' ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Desde" }), _jsx("div", { className: "w-44", children: _jsx(DatePicker, { value: rangoDesde, onChange: (v) => {
                                                        setRangoDesde(v);
                                                        setPagina(1);
                                                    } }) })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Hasta" }), _jsx("div", { className: "w-44", children: _jsx(DatePicker, { value: rangoHasta, onChange: (v) => {
                                                        setRangoHasta(v);
                                                        setPagina(1);
                                                    } }) })] })] })) : null, _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Buscar" }), _jsxs("div", { className: "relative w-64", children: [_jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" }), _jsx(Input, { value: busquedaCodigo, onChange: (e) => {
                                                    setBusquedaCodigo(e.target.value);
                                                    setPagina(1);
                                                }, placeholder: vistaPromotor
                                                    ? 'Código o cliente'
                                                    : 'Código, nombre, email o teléfono', className: "h-9 pl-8 text-xs" })] })] }), vistaPromotor ? null : (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Cliente" }), _jsx(ClienteAutocomplete, { clienteNombre: cliente?.nombre, onChange: (c) => {
                                            setCliente(c);
                                            setPagina(1);
                                        } })] })), vistaPromotor ? null : (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Sucursal" }), _jsxs(Select, { value: sucursalId, onChange: (e) => {
                                            setSucursalId(e.target.value);
                                            setPagina(1);
                                        }, className: "w-44", children: [_jsx("option", { value: "", children: "Todas" }), sucursales?.map((s) => (_jsx("option", { value: s.id, children: s.nombre }, s.id)))] })] })), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Estado" }), _jsxs(Select, { value: estado, onChange: (e) => {
                                            setEstado(e.target.value);
                                            setPagina(1);
                                        }, className: "w-40", children: [_jsx("option", { value: "", children: "Todos" }), ESTADOS_RESERVA.map((e) => (_jsx("option", { value: e, children: LABEL_ESTADO_RESERVA[e] ?? e }, e)))] })] }), vistaPromotor ? (_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Pago" }), _jsxs(Select, { value: estadoPago, onChange: (e) => {
                                            setEstadoPago(e.target.value);
                                            setPagina(1);
                                        }, className: "w-40", children: [_jsx("option", { value: "", children: "Todos" }), _jsx("option", { value: "pagado", children: "Pagada" }), _jsx("option", { value: "parcial", children: "Pago parcial" }), _jsx("option", { value: "pendiente", children: "Pendiente" })] })] })) : null, vistaPromotor && totales ? (_jsx("div", { className: "ml-auto flex items-center gap-4 text-right", children: _jsxs("div", { children: [_jsx("div", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Comisi\u00F3n total \u00B7 esta p\u00E1gina" }), _jsx("div", { className: "num text-sm font-semibold text-success", children: formatearMonto(totales.comision) })] }) })) : null] })] }), isError ? (_jsxs("div", { className: "rounded-lg border border-danger bg-danger-fill px-4 py-3 text-sm text-danger", children: [error instanceof AppError ? error.mensaje : 'No pudimos cargar las reservas.', _jsx("button", { onClick: () => refetch(), className: "ml-2 underline", children: "Reintentar" })] })) : (_jsxs(_Fragment, { children: [_jsx(Table, { columns: columnas, items: itemsFiltrados, rowKey: (r) => r.id, onRowClick: (r) => setPreviewReserva(r), cargando: isLoading, vacioTitulo: "Sin reservas con esos filtros", vacioDescripcion: "Cambi\u00E1 el per\u00EDodo o sac\u00E1 los filtros.", vacioAccion: puedeCrear ? (_jsxs(Button, { onClick: () => navigate('/reservas/nueva'), children: [_jsx(Plus, { className: "h-4 w-4" }), "Nueva reserva"] })) : null }), data ? (_jsx(Pagination, { pagina: data.paginacion.pagina, totalPaginas: data.paginacion.totalPaginas, total: data.paginacion.total, porPagina: data.paginacion.porPagina, onCambioPagina: setPagina })) : null] })), pagoReserva ? (_jsx(RegistrarPagoModal, { open: Boolean(pagoReserva), onOpenChange: (open) => !open && setPagoReserva(null), reservaId: pagoReserva.id, sucursalId: pagoReserva.sucursalId, saldoPendiente: pagoReserva.totalNeto, metodoSugerido: pagoReserva.metodoPagoPrevisto })) : null, _jsx(CancelarReservaModal, { open: Boolean(cancelarReserva), onOpenChange: (open) => !open && setCancelarReserva(null), reservaId: cancelarReserva?.id ?? null, codigo: cancelarReserva?.codigo }), _jsx(ReservaPreviewModal, { reserva: previewReserva, onClose: () => setPreviewReserva(null) })] }));
}
function ResumenClases({ resumen, }) {
    if (!resumen || resumen.total === 0) {
        return _jsx("span", { className: "text-[11px] text-text-tertiary", children: "\u2014" });
    }
    // Tono: warning si hay sin asignar, danger si solo canceladas, success si todo bien.
    const tono = resumen.sinInstructor > 0
        ? 'warning'
        : resumen.canceladas > 0 && resumen.conInstructor === 0
            ? 'danger'
            : 'success';
    return _jsx(Chip, { tono: tono, children: resumen.total });
}
function ClaseDestacadaLine({ info, }) {
    const f = formatearFecha(info.fechaHoraInicio);
    const h = formatearHora(info.fechaHoraInicio);
    const tipoLegible = info.tipoClase
        ? (LABEL_TIPO_CLASE[info.tipoClase] ?? info.tipoClase)
        : null;
    const desc = [info.disciplina, tipoLegible].filter(Boolean).join(' · ');
    return (_jsxs("span", { className: "text-[10px] text-text-tertiary", children: [f, " ", h, desc ? ` · ${desc}` : ''] }));
}
